import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en/translation.json';
import hi from './locales/hi/translation.json';

export const LANGUAGE_STORAGE_KEY = 'appLanguage';
export const DEFAULT_LANGUAGE = 'en';
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी' },
];

const supportedCodes = SUPPORTED_LANGUAGES.map(lang => lang.code);

const getStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return supportedCodes.includes(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    hi: { translation: hi },
  },
  lng: getStoredLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: supportedCodes,
  returnEmptyString: false,
  returnNull: false,
  interpolation: {
    escapeValue: false, // React already escapes rendered values
  },
  react: {
    useSuspense: false,
  },
});

document.documentElement.lang = i18n.language;

i18n.on('languageChanged', lng => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch {
    // storage unavailable; language still applies for this session
  }
  document.documentElement.lang = lng;
});

// Menu labels come from the backend. Known default labels are translated via the `menu`
// namespace, then via any existing UI text with the same wording. Matching ignores case,
// spaces and punctuation ("On-Boarding" = "Onboarding", "Timetable" = "Time Table").
// Anything else (e.g. a school's custom label) is shown unchanged.
const normalizeLabel = label => String(label || '').toLowerCase().replace(/[^a-z0-9]/g, '');

let menuKeyIndex = null;
const getMenuKeyIndex = () => {
  if (menuKeyIndex) return menuKeyIndex;
  menuKeyIndex = new Map();
  const add = (text, key) => {
    const normalized = normalizeLabel(text);
    if (normalized && !menuKeyIndex.has(normalized)) menuKeyIndex.set(normalized, key);
  };
  // `menu` entries take priority over general UI text
  Object.entries(en.menu || {}).forEach(([k, v]) => add(v, `menu.${k}`));
  const walk = (node, prefix) => {
    Object.entries(node).forEach(([k, v]) => {
      const key = prefix ? `${prefix}.${k}` : k;
      if (typeof v === 'string') add(v, key);
      else if (v && typeof v === 'object') walk(v, key);
    });
  };
  walk(en, '');
  return menuKeyIndex;
};

export const translateMenuLabel = (label, t = i18n.t.bind(i18n)) => {
  if (typeof label !== 'string' || !label.trim()) return label;
  const key = getMenuKeyIndex().get(normalizeLabel(label));
  if (key) return t(key);
  // "Users & Roles (beta)": translate the base label and keep the bracketed suffix
  const match = label.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
  if (match) {
    const baseKey = getMenuKeyIndex().get(normalizeLabel(match[1]));
    if (baseKey) {
      const suffixKey = `menuSuffix.${match[2].trim().toLowerCase()}`;
      const suffix = i18n.exists(suffixKey) ? t(suffixKey) : match[2];
      return `${t(baseKey)} (${suffix})`;
    }
  }
  return label;
};

export default i18n;
